import { createRoute } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Button } from "../components/ui/Button/Button";
import { Badge } from "../components/ui/Badge/Badge";
import { ConfirmDialog } from "../components/ui/ConfirmDialog/ConfirmDialog";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import { BackButton } from "../components/ui/BackButton/BackButton";
import { useActivityDetail, useUpdateActivity, useDeleteActivity } from "../hooks/use-activities";
import { formatDate } from "../lib/date";
import styles from "./ActivityDetail.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/activities/$id",
	component: withPermission(ActivityDetailPage, "activities", "read"),
});

function ActivityDetailPage() {
	const { id } = route.useParams();
	const { t } = useTranslation();
	const activityQuery = useActivityDetail(id);
	const updateActivity = useUpdateActivity();
	const deleteActivity = useDeleteActivity();
	const [deleteOpen, setDeleteOpen] = useState(false);

	if (activityQuery.isLoading) return <LoadingSpinner />;
	if (activityQuery.isError || !activityQuery.data)
		return <ErrorFallback onRetry={activityQuery.refetch} />;

	const activity = activityQuery.data;

	const toggleComplete = () => {
		updateActivity.mutate(
			{
				id,
				data: {
					isCompleted: !activity.isCompleted,
					completedDate: !activity.isCompleted ? new Date().toISOString() : null,
				},
			},
			{
				onSuccess: () => {
					alert(activity.isCompleted ? t("markIncompleteSuccess") : t("markCompleteSuccess"));
				},
			},
		);
	};

	return (
		<div>
			<BackButton fallbackTo="/activities" />
			<PageHeader
				title={activity.subject}
				action={
					<div className={styles.actions}>
						<Button variant="secondary" onClick={toggleComplete}>
							{activity.isCompleted ? t("markIncomplete") : t("markComplete")}
						</Button>
						<Button variant="danger" onClick={() => setDeleteOpen(true)}>
							{t("delete")}
						</Button>
					</div>
				}
			/>

			<div className={styles.grid}>
				<Card header={t("generalInfo")}>
					<div className={styles.infoGrid}>
						<Info label={t("type")} value={activity.type} />
						<Info label={t("subject")} value={activity.subject} />
						<Info
							label={t("dueDate")}
							value={activity.dueDate ? formatDate(activity.dueDate) : "-"}
						/>
						<Info
							label={t("completedDate")}
							value={activity.completedDate ? formatDate(activity.completedDate) : "-"}
						/>
						<div>
							<span className={styles.infoLabel}>{t("status")}</span>
							<Badge variant={activity.isCompleted ? "success" : "warning"}>
								{activity.isCompleted ? t("completed") : t("pending")}
							</Badge>
						</div>
					</div>
				</Card>

				<Card header={t("content")}>
					<p className={styles.content}>{activity.content || t("noData")}</p>
				</Card>
			</div>

			<ConfirmDialog
				isOpen={deleteOpen}
				onClose={() => setDeleteOpen(false)}
				onConfirm={() =>
					deleteActivity.mutate(id, {
						onSuccess: () => {
							setDeleteOpen(false);
							alert(t("deleteSuccess"));
						},
					})
				}
				message={t("confirmDelete")}
				isLoading={deleteActivity.isPending}
			/>
		</div>
	);
}

function Info({ label, value }: { label: string; value: string | null }) {
	return (
		<div>
			<span className={styles.infoLabel}>{label}</span>
			<p className={styles.infoValue}>{value || "-"}</p>
		</div>
	);
}
